import type React from 'react';
import { useContext, useMemo } from 'react';
import { ForgeCheckbox, ForgeTextField } from '@tylertech/forge-react';
import { I18n } from 'common/i18n';
import { Key } from 'common/types/keyboard/key';
import { SoQLType } from 'common/types/soql';
import { getParameterHumanText } from '../lib/Filters';
import { ParameterItemContext } from './ParameterItemContext';

export const ParameterItemControl = ({
  onUpdate,
}: {
  onUpdate: (parameterOverrideValue: string) => void;
}) => {
  const { parameter, clientContextVariables, editMode, setOpen, open, toggleRef } =
    useContext(ParameterItemContext)!;

  const paramText = useMemo(
    () => getParameterHumanText(parameter, clientContextVariables),
    [clientContextVariables, parameter],
  );

  const anchorId = `parameter-${parameter.paramIds[0].name}-${parameter.paramIds[0].datasetUid}-anchor`;

  const toggle = () => setOpen(!open);

  const onKeyDownControl = (event: React.KeyboardEvent) => {
    const openKeys = [Key.ArrowDown, Key.Enter, Key.Space];
    if (openKeys.includes(event.key as Key)) {
      event.stopPropagation();
      event.preventDefault();
      toggle();
    }
  };

  const onKeyUpControl = (event: React.KeyboardEvent) => {
    const openKeys = [Key.ArrowDown, Key.Enter, Key.Space];
    if (openKeys.includes(event.key as Key)) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  if (parameter.dataType === SoQLType.SoQLBooleanT) {
    return (
      <div className="boolean-parameter-container">
        <div className={editMode ? 'boolean-parameter' : ''}>
          <ForgeCheckbox
            checked={parameter.overrideValue === 'true' ? true : false}
            dense
            id={`parameter-${parameter.paramIds[0].name}-checkbox`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(e.target.checked.toString())}
          >
            {paramText}
          </ForgeCheckbox>
        </div>
      </div>
    );
  }

  return (
    <ForgeTextField
      density="small"
      id={anchorId}
      label-position="block-start"
      popoverIcon
      required
      variant="filled"
    >
      <label className="parameter-label">{parameter.displayName}</label>
      <input
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`${I18n.t('shared.components.filter_bar.parameter')} ${parameter.displayName}`}
        aria-required="true"
        onKeyDown={onKeyDownControl}
        onKeyUp={onKeyUpControl}
        readOnly
        ref={toggleRef}
        role="combobox"
        value={paramText}
      />
    </ForgeTextField>
  );
};
